import { memo, useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";

import { JsonEditor } from "../../../../components/JsonEditor";
import type { IChoiceState } from "./useChoicesState";

export interface IPromptJsonEditorProps {
  initialValue: string;
  isLoading: boolean;
  onSave: (value: any) => void;
  onChange: (value: { content: any }) => void;
  setHasChanges?: (value: boolean) => void;
  answerData: IChoiceState;
}

const AnswerJsonEditor: React.FC<IPromptJsonEditorProps> = memo(
  ({
    initialValue,
    isLoading,
    onSave,
    onChange,
    setHasChanges,
    answerData,
  }) => {
    const [isError, setIsError] = useState(false);
    const [updatedAnswer, setUpdatedAnswer] = useState<string>("");
    const [isCustomUndo, setIsCustomUndo] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);

    useEffect(() => {
      onChange({ content: JSON.parse(initialValue) });
      setUpdatedAnswer(initialValue);
      setIsCustomUndo(initialValue !== answerData.startValue);
    }, [answerData, onChange, setUpdatedAnswer, setIsCustomUndo, initialValue]);

    useEffect(() => {
      setHasChanges && setHasChanges(answerData.startValue !== updatedAnswer);
    }, [updatedAnswer, answerData, setHasChanges]);

    const handleOnChange = (e: any) => {
      if (!e) {
        setUpdatedAnswer("");
      } else {
        setUpdatedAnswer(JSON.stringify(e));
      }
      onChange({ content: e });
    };

    const handleError = (value: boolean) => {
      setIsError(value);
    };

    const handleClickSave = () => {
      onSave(updatedAnswer);
    };

    useEffect(() => {
      setIsDisabled(
        answerData.startValue === updatedAnswer ||
          isError ||
          !updatedAnswer ||
          isLoading,
      );
    }, [updatedAnswer, isError, isLoading, answerData.startValue]);

    return (
      <div data-testid="json-editor">
        <JsonEditor
          initialValue={JSON.parse(initialValue)}
          onChange={handleOnChange}
          onError={handleError}
          hasChanges={updatedAnswer !== answerData.startValue}
          isCustomUndo={isCustomUndo}
          startValue={answerData.startValue}
          setCustomUndo={setIsCustomUndo}
          shoMenuBar
        />
        <div className="mt-5 d-flex justify-content-end gap-5 ">
          <Button
            onClick={handleClickSave}
            variant="primary"
            disabled={isDisabled}
          >
            Save
            {isLoading && (
              <Spinner className="ms-2" size="sm" animation="border" />
            )}
          </Button>
        </div>
      </div>
    );
  },
);

export { AnswerJsonEditor };
